// Aufklappbare Raum-Karte im Rundgang (Schritt 2) — Kopfzeile „Raum · n Mängel",
// Prüfpunkte, im Raum erfasste Zähler sowie „Zähler hier erfassen" (Picker + freie
// Neuanlage). Alle Zustandsänderungen laufen über Callback-Props aus SchrittRundgang;
// diese Karte selbst hält nur UI-lokalen Zustand (auf/zu, Mini-Formular).
import React, { useState } from 'react';
import { CaretDown, Plus, Trash } from '@phosphor-icons/react';
import { raumStil, raumUnterzeile } from '../../../lib/uebergabeAnsicht/darstellung';
import { FotoKachel, FotoReihe, RAUM_ICON, istTouch, schriftAuf } from './assistent/Rundgangteile';
import type { Pruefpunkt, RaumBlock, ZaehlerEintrag } from '../../../lib/uebergabe/typen';
import type { ZaehlerStammdaten } from '../../../lib/uebergabe/zaehlerZuordnung';
import PruefpunktZeile from './PruefpunktZeile';
import ZaehlerZeile from './ZaehlerZeile';
import FotoVorschau from './FotoVorschau';

const LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' };
const FELD: React.CSSProperties = { height: 44, padding: '0 12px', borderRadius: 12, border: 'none', outline: 'none', background: 'var(--fill2)',
  fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)', minWidth: 0 };

const SONSTIGER_RAUM = 'Sonstiger Raum';

interface RaumKarteProps {
  raumBlock: RaumBlock;
  /** Anzeigename inkl. laufender Nummer bei gleichnamigen Raumblöcken — von
   * SchrittRundgang.tsx zentral über nummeriereGleicheNamen() berechnet (dort allein ist
   * die vollständige Raumliste bekannt, hier nicht). */
  anzeigeName: string;
  fotos: Record<string, Blob>;
  stammZeilen: ZaehlerStammdaten[];
  adHocZeilen: ZaehlerEintrag[];
  weitereZaehler: ZaehlerStammdaten[];
  holeEintrag: (stammId: string) => ZaehlerEintrag | undefined;
  aenderePruefpunkt: (name: string, patch: Partial<Pruefpunkt>) => void;
  fotosHinzufuegen: (name: string, dateien: FileList) => void;
  /** Raum-Fotos zur Zustandsdokumentation, unabhängig vom Mangel-Status einzelner Prüfpunkte. */
  raumFotosHinzufuegen: (dateien: FileList) => void;
  /** Freitext-Bezeichnung bei „Sonstiger Raum" (leer = bleibt „Sonstiger Raum"). */
  raumBezeichnungAendern: (wert: string) => void;
  standAendernStamm: (stamm: ZaehlerStammdaten, stand: string) => void;
  fotoHinzufuegenStamm: (stamm: ZaehlerStammdaten, datei: File) => void;
  standAendernAdHoc: (id: string, stand: string) => void;
  fotoHinzufuegenAdHoc: (id: string, datei: File) => void;
  zaehlerZuRaumHinzufuegen: (stamm: ZaehlerStammdaten) => void;
  neuerZaehlerHinzufuegen: (art: string, nummer: string) => void;
  raumEntfernen: () => void;
}

const RaumKarte: React.FC<RaumKarteProps> = ({
  raumBlock, anzeigeName, fotos, stammZeilen, adHocZeilen, weitereZaehler, holeEintrag,
  aenderePruefpunkt, fotosHinzufuegen, raumFotosHinzufuegen, raumBezeichnungAendern,
  standAendernStamm, fotoHinzufuegenStamm,
  standAendernAdHoc, fotoHinzufuegenAdHoc, zaehlerZuRaumHinzufuegen, neuerZaehlerHinzufuegen, raumEntfernen,
}) => {
  const [offen, setOffen] = useState(true);
  const [zeigeWeitere, setZeigeWeitere] = useState(false);
  const [neuerArt, setNeuerArt] = useState('');
  const [neuerNummer, setNeuerNummer] = useState('');
  // `?? []`: ein Raumblock aus einem älteren, lokal begonnenen Entwurf könnte
  // `pruefpunkte` selbst vermissen lassen (s. lib/uebergabe/lokal.ts) — ungeschützt
  // stürzt bereits das Rendern dieser Karte ab.
  const pruefpunkte = raumBlock.pruefpunkte ?? [];
  const raumFotos = raumBlock.fotos ?? [];
  const stil = raumStil(raumBlock.raum);
  const RI = RAUM_ICON[stil.symbol];
  const unter = raumUnterzeile(raumBlock);
  const unterFarbe = unter.ton === 'bad' ? 'var(--bad)' : unter.ton === 'ok' ? 'var(--ok)' : 'var(--ink3)';
  const touch = istTouch();

  return (
    <section aria-label={anzeigeName} className="immo-glas flex min-w-0 flex-col" style={{ borderRadius: 24, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOffen(o => !o)} aria-expanded={offen} className="flex w-full items-center text-left"
        style={{ minHeight: 64, padding: '12px 14px', gap: 12, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)' }}>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 40, height: 40, borderRadius: 12, background: stil.farbe, color: schriftAuf(stil.farbe) }}>
          <RI size={20} weight="fill" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
          <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{anzeigeName}</span>
          <span className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: unterFarbe }}>{unter.text}</span>
        </span>
        {pruefpunkte.length > 0 && (
          <span aria-hidden className="hidden shrink-0 items-center sm:inline-flex" style={{ gap: 3 }}>
            {pruefpunkte.map(pp => (
              <span key={pp.name} style={{ width: 7, height: 7, borderRadius: '50%',
                background: pp.status === 'io' ? 'var(--ok)' : pp.status === 'mangel' ? 'var(--bad)' : 'var(--fill3, var(--fill2))' }} />
            ))}
          </span>
        )}
        <CaretDown size={16} weight="bold" color="var(--ink3)" className="shrink-0" style={{ transform: offen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </button>
      {offen && (
        <div className="flex flex-col" style={{ padding: '0 14px 16px', gap: 14 }}>
          {raumBlock.raum === SONSTIGER_RAUM && (
            <label className="flex flex-col" style={{ gap: 6 }}>
              <span style={LABEL}>Bezeichnung</span>
              <input value={raumBlock.bezeichnung ?? ''} onChange={e => raumBezeichnungAendern(e.target.value)} placeholder="z. B. Hobbyraum, Speisekammer" style={FELD} />
            </label>
          )}

          <div className="flex flex-col" style={{ gap: 8 }}>
            <span style={LABEL}>Fotos: {anzeigeName}</span>
            <FotoReihe>
              {raumFotos.map(pfad => (fotos[pfad] ? <FotoVorschau key={pfad} blob={fotos[pfad]} /> : null))}
              <FotoKachel tip="Foto – Raum fotografieren oder Bilder wählen (auch ohne Mangel)" onDateien={raumFotosHinzufuegen} />
            </FotoReihe>
          </div>

          {pruefpunkte.length > 0 && (
            <div className="ios-gruppe flex flex-col overflow-hidden" style={{ borderRadius: 18, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
              {pruefpunkte.map(pp => (
                <PruefpunktZeile
                  key={pp.name} pp={pp} fotos={fotos}
                  aendere={patch => aenderePruefpunkt(pp.name, patch)}
                  fotosHinzufuegen={dateien => fotosHinzufuegen(pp.name, dateien)}
                />
              ))}
            </div>
          )}

          {(stammZeilen.length > 0 || adHocZeilen.length > 0) && (
            <div className="flex flex-col" style={{ gap: 8 }}>
              <span style={LABEL}>Zähler in diesem Raum</span>
              {stammZeilen.map(z => {
                const eintrag = holeEintrag(z.id);
                return (
                  <ZaehlerZeile
                    key={z.id} art={z.typ} nummer={z.zaehler_nummer} stand={eintrag?.stand ?? ''} foto={eintrag?.foto ?? null} fotos={fotos}
                    standAendern={stand => standAendernStamm(z, stand)}
                    fotoHinzufuegen={datei => fotoHinzufuegenStamm(z, datei)}
                  />
                );
              })}
              {adHocZeilen.map(z => (
                <ZaehlerZeile
                  key={z.id} art={z.art} nummer={z.nummer} stand={z.stand} foto={z.foto} fotos={fotos}
                  standAendern={stand => standAendernAdHoc(z.id, stand)}
                  fotoHinzufuegen={datei => fotoHinzufuegenAdHoc(z.id, datei)}
                />
              ))}
            </div>
          )}

          <div className="flex flex-col" style={{ gap: 8 }}>
            <button type="button" onClick={() => setZeigeWeitere(v => !v)} aria-expanded={zeigeWeitere} className="inline-flex items-center self-start"
              style={{ height: touch ? 44 : 36, padding: '0 12px', gap: 6, borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                background: 'var(--acc-soft)', color: 'var(--acc)' }}>
              <Plus size={13} weight="bold" /> Zähler hier erfassen
            </button>
            {zeigeWeitere && (
              <div className="flex flex-col" style={{ gap: 8 }}>
                {weitereZaehler.map(z => (
                  <button key={z.id} type="button" onClick={() => zaehlerZuRaumHinzufuegen(z)} className="immo-zeile-hover flex items-center justify-between text-left"
                    style={{ minHeight: 44, padding: '0 12px', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--fill)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>
                    <span>{z.typ} · {z.zaehler_nummer}</span>
                    <Plus size={14} weight="bold" color="var(--acc)" />
                  </button>
                ))}
                <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                  <input value={neuerArt} onChange={e => setNeuerArt(e.target.value)} placeholder="Art" aria-label="Art" style={{ ...FELD, flex: '1 1 120px' }} />
                  <input value={neuerNummer} onChange={e => setNeuerNummer(e.target.value)} placeholder="Nummer" aria-label="Nummer" style={{ ...FELD, flex: '1 1 120px' }} />
                  <button type="button" onClick={() => { neuerZaehlerHinzufuegen(neuerArt, neuerNummer); setNeuerArt(''); setNeuerNummer(''); }}
                    style={{ height: 44, padding: '0 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                      background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', flexShrink: 0 }}>
                    + Neuer Zähler
                  </button>
                </div>
              </div>
            )}
          </div>

          <button type="button" onClick={raumEntfernen} data-tip="Raum entfernen – Raum und seine Angaben aus dem Protokoll nehmen" className="inline-flex items-center self-start"
            style={{ height: 44, padding: '0 4px', gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, color: 'var(--bad)' }}>
            <Trash size={14} weight="bold" /> Raum entfernen
          </button>
        </div>
      )}
    </section>
  );
};

export default RaumKarte;
